import {
  ascii,
  blank,
  duplicate,
  format,
  fullWidthFormat,
  kana,
  maxNum,
  maxNumStr,
  minNum,
  rangeNum,
} from './message';

export type FormError = {
  message: string;
  isError: boolean;
  isTouched: boolean;
};

export const validBlank = {
  check: (value: string): boolean => {
    return value === '';
  },
  message: (label: string): string => blank(label),
};

export const validUrlFormat = {
  check: (url: string): boolean => {
    if (url === '') return false;
    const urlPattern =
      /^(https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}\/?([^\s]*)$/;
    return !urlPattern.test(url);
  },
  message: (label: string): string => format(label),
};

export const validMaxStr = {
  check: (value: string, num: number): boolean => {
    return value.length > num;
  },
  message: (label: string, num: number): string => maxNumStr(label, num),
};

export const validMaxNum = {
  check: (value: number, num: number): boolean => {
    return value > num;
  },
  message: (label: string, num: number): string => maxNum(label, num),
};

export const validMinNum = {
  check: (value: number, num: number): boolean => {
    return value < num;
  },
  message: (label: string, num: number): string => minNum(label, num),
};

export const validNumericFormat = {
  check: (value: string): boolean => {
    const regExp = /^[0-9]+$/;
    return !regExp.test(value);
  },
  message: (label: string): string => format(label),
};

export const validNumInRange = {
  check: (value: number, min: number, max: number): boolean => {
    return value < min || value > max;
  },
  message: (label: string, min: number, max: number): string =>
    rangeNum(label, min, max),
};

export const validDuplicate = {
  message: (label: string): string => duplicate(label),
};

export const validatePhoneNumberFormat = {
  check: (value: string): boolean => {
    // NOTE 参考記事：https://mebee.info/2022/03/11/post-61281/#outline__1
    const regExp = /^0\d{9,10}$/;
    return !regExp.test(value);
  },
  message: (label: string): string => format(label),
};

export const validateZipCodeFormat = {
  check: (value: string): boolean => {
    const regExp = /\d{7}$/;
    return !regExp.test(value);
  },
  message: (label: string): string => format(label),
};

export const validHalfWidthFormat = {
  check: (value: string): boolean => {
    return !!RegExp(/[\u0020-\u007E\uFF65-\uFF9F]/).exec(value);
  },
  message: (label: string): string => fullWidthFormat(label),
};

export const validAsciiFormat = {
  check: (value: string): boolean => {
    const regExp = /^[\x21-\x7E]*$/;
    return regExp.test(value);
  },
  message: (label: string): string => ascii(label),
};

export const validAlphabetFormat = {
  check: (value: string): boolean => {
    const regExp = /^[a-zA-Z]+$/;
    return !regExp.test(value);
  },
  message: (label: string): string => format(label),
};

export const validEmailFormat = {
  check: (value: string): boolean => {
    // NOTE 参考記事：https://mebee.info/2022/03/11/post-61281/#outline__1
    const regExp = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return !regExp.test(value);
  },
  message: (label: string): string => format(label),
};

export const validOverStartHours = {
  check: (start: string, end: string): boolean => {
    const currentDate = new Date().toDateString();
    const startDate = new Date(`${currentDate} ${start}`);
    const endDate = new Date(`${currentDate} ${end}`);
    return startDate.getTime() >= endDate.getTime();
  },
  message: (label: string): string => format(label),
};

export const validKana = {
  check: (value: string): boolean => {
    return !/^[\u30A0-\u30FFー]*$/.test(value);
  },
  message: (label: string): string => kana(label),
};

export const validRegexFormat = {
  check: (value: string): boolean => {
    try {
      new RegExp(value);
      return false;
    } catch (e) {
      return true;
    }
  },
  message: (label: string): string => format(label),
};

// 郵便番号のバリデーション
// 7桁の数字 or 末尾にアスタリスク(*)がある2桁以上の数字
export const validatePostalCodeFormat = {
  check: (value: string): boolean => {
    const regExp = /^\d{7}$|^\d{2,}\*$/;
    return !regExp.test(value);
  },
  message: (label: string): string => format(label),
};
